import { CandidateListData, RemoveRequestPayload, RemoveRequestResponse } from './../types/api';
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, CancelTokenSource } from 'axios';
import { toast } from 'react-toastify';
import { store } from '../store';
import { logoutUser, setSuspended, setDomainVerified } from '../store/slices/authSlice';
import { performEmergencyLogout } from '../utils/logout';
import { getCachedEncodedHeaders } from '../utils/headerEncryption';
import { getCompanyS3Folder } from '../utils/s3Upload';
import Config from '../Config';
import {
  LoginResponse,
  BasicDetailsResponse,
  CompanyDetailsResponse,
  LookingForResponse,
  DesignationsResponse,
  WorkExperienceResponse,
  SkillsResponse,
  CompaniesResponse,
  CreditBalanceResponse,
  DashboardDataResponse,
  PitchAnalyticsPeriod,
  CreditFeaturesResponse,
  CandidateListResponse,
  ProfileResponse,
  ApiResponse,
  ChatAllMatched,
  ConnectResponse,
  OnboardingProgressResponse,
  SendOTPResponse,
  VerifyOTPResponse,
  DomainVerificationResponse,
  GSTVerificationResponse,
  VerificationOrderRazorpayResponse,
  VerificationIntentStripeResponse,
  VerifyVerificationPaymentResponse,
} from '../types/api';
import Cookies from 'js-cookie';
import { VerifyGstRequest, VerifyGstResponse } from 'types/onboarding';


// Cache interface
interface CacheEntry {
  data: any;
  timestamp: number;
  expiry: number;
}

// Request retry configuration
interface RetryConfig {
  retries: number;
  retryDelay: number;
  retryCondition?: (error: any) => boolean;
}

// API Configuration
const API_BASE_URL = Config.APIBASEURL;

class ApiService {
  private api: AxiosInstance;
  private cache: Map<string, CacheEntry> = new Map();
  private pendingRequests: Map<string, Promise<any>> = new Map();
  private cancelTokens: Map<string, CancelTokenSource> = new Map();
  private isHandling401: boolean = false;

  private normalizeDateOnly(value: any): string | null {
    if (value === undefined || value === null || value === '') {
      return null;
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return null;

      const isoDateMatch = trimmed.match(/^(\d{4}-\d{2}-\d{2})/);
      if (isoDateMatch) {
        return isoDateMatch[1];
      }
    }

    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
      return null;
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000, // Increased timeout for better UX
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
    this.setupCacheCleanup();
  }

  private setupInterceptors() {
    // Request interceptor - Add auth token and dynamic headers
    this.api.interceptors.request.use(
      async (config) => {
        const state = store.getState();
        const token = state.auth.token ||
          localStorage.getItem("token") ||
          localStorage.getItem("authToken") ||
          localStorage.getItem("auth_token") ||
          localStorage.getItem("login_otp_auth_token") ||
          sessionStorage.getItem("token") ||
          sessionStorage.getItem("authToken") ||
          sessionStorage.getItem("auth_token") ||
          "";

        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Get real-time encoded headers
        try {
          const encodedHeaders = await getCachedEncodedHeaders();

          config.headers.latitude = encodedHeaders.latitude;
          config.headers.longitude = encodedHeaders.longitude;
          config.headers.devicename = encodedHeaders.devicename;
          config.headers.ip = encodedHeaders.ip;
          config.headers.version = encodedHeaders.version;
          config.headers.address = encodedHeaders.address;
        } catch (error) {
          console.error('Failed to generate encoded headers:', error);
          // Fallback to default values if header generation fails
          config.headers.latitude = 'YWFhYTAuMGFhYWE='; // Encoded "0.0"
          config.headers.longitude = 'YWFhYTAuMGFhYWE='; // Encoded "0.0"
          config.headers.devicename = 'YWFhYVdlYmFhYWE='; // Encoded "Web"
          config.headers.ip = 'YWFhYTAuMC4wLjBhYWFh'; // Encoded "0.0.0.0"
          config.headers.version = 'YWFhYTEuMC4wYWFhYQ=='; // Encoded "1.0.0"
          config.headers.address = 'YWFhYVVua25vd24gTG9jYXRpb25hYWFh'; // Encoded "Unknown Location"
        }

        // Static API key (this should remain constant)
        config.headers.apiKey = 'MTIzNDQ5NmQ0ZjliOS01ZWYxLTExZWYtYmIzMi0wNjYxMjhmNjk4NjUxMjM0NA==';

        // Add request ID for tracking (using custom property)
        (config as any).metadata = {
          requestId: this.generateRequestId(),
          startTime: Date.now()
        };

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor - Handle errors globally with retry logic
    this.api.interceptors.response.use(
      (response: AxiosResponse) => {
        // Log successful requests in development
        if (process.env.NODE_ENV === 'development') {
          const duration = Date.now() - ((response.config as any).metadata?.startTime || 0);

        }        // --- CREDIT BALANCE REFRESH (event-driven, not polling) ---
        try {
          const data = response.data;
          const url = response.config?.url || '';

          // 1. Response body explicitly contains credit deduction info
          const hasCreditKeys = data && typeof data === 'object' && (
            'credits_deducted' in data ||
            'creditsDeducted' in data ||
            'available_credits' in data ||
            'availableCredits' in data ||
            'remaining_credits' in data ||
            'remainingCredits' in data ||
            'available_credits_after' in data ||
            'availableCreditsAfter' in data ||
            (data.data && typeof data.data === 'object' && (
              'credits_deducted' in data.data ||
              'creditsDeducted' in data.data ||
              'available_credits' in data.data ||
              'availableCredits' in data.data
            ))
          );

          const method = (response.config?.method || 'get').toLowerCase();
          const isGetRequest = method === 'get';
          const creditsDeducted = data?.credits_deducted || data?.creditsDeducted || data?.data?.credits_deducted || data?.data?.creditsDeducted;

          if (!isGetRequest && creditsDeducted && Number(creditsDeducted) > 0) {
            import('../utils/creditEvents').then(({ notifyCreditChange }) => {
              notifyCreditChange(Number(creditsDeducted));
            }).catch(() => {});

            const featureUsed = data?.feature_used || data?.data?.feature_used;
            const featureName = featureUsed
              ? String(featureUsed).replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase())
              : 'Premium Feature Usage';

            toast.info(`-${creditsDeducted} Credits for ${featureName}`, {
              position: 'top-right',
              autoClose: 3500,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
            });
          }
        } catch (e) {
          console.error('Error checking credits in interceptor:', e);
        }

        // Check account suspension status from response data
        try {
          const resData = response.data;
          const isSuspended =
            resData?.is_suspended ??
            resData?.isSuspended ??
            resData?.data?.is_suspended ??
            resData?.data?.isSuspended;
          if (isSuspended !== undefined && isSuspended !== null) {
            store.dispatch(setSuspended(Boolean(isSuspended)));
          }
        } catch (e) {
          // ignore
        }

        // Check domain verification status from response headers or data
        try {
          const headerDomainVerified = response.headers?.['x-domain-verified'];
          let domainVerifiedVal: boolean | null = null;
          if (headerDomainVerified !== undefined && headerDomainVerified !== null) {
            domainVerifiedVal = headerDomainVerified === 'true' || headerDomainVerified === '1';
          } else if (response.data && typeof response.data === 'object') {
            const resData = response.data;
            const val =
              resData?.domain_verified ??
              resData?.domainVerified ??
              resData?.verified_domain ??
              resData?.data?.domain_verified ??
              resData?.data?.domainVerified ??
              resData?.data?.verified_domain;
            if (val !== undefined && val !== null) {
              domainVerifiedVal = Number(val) === 1 || val === true || val === 'true';
            }
          }

          if (domainVerifiedVal !== null) {
            const currentVal = localStorage.getItem('verified_domain');
            const newValStr = domainVerifiedVal ? 'true' : 'false';
            if (currentVal !== newValStr) {
              localStorage.setItem('verified_domain', newValStr);
              store.dispatch(setDomainVerified(domainVerifiedVal));
            }
          }

          // Check if backend automatically refreshed the token (< 1 day remaining)
          const refreshedToken = response.headers?.['x-refreshed-token'];
          if (refreshedToken) {
            try {
              localStorage.setItem('auth_token', refreshedToken);
              localStorage.setItem('authToken', refreshedToken);
              localStorage.setItem('token', refreshedToken);
              sessionStorage.setItem('auth_token', refreshedToken);
              sessionStorage.setItem('authToken', refreshedToken);
              sessionStorage.setItem('token', refreshedToken);
            } catch (refErr) {
              console.error('Error updating refreshed token:', refErr);
            }
          }
        } catch (e) {
          // ignore
        }

        return response;
      },
      async (error) => {
        const { response, config } = error;

        // Log failed requests in development
        if (process.env.NODE_ENV === 'development') {
          const duration = Date.now() - ((config as any)?.metadata?.startTime || 0);
          console.error(`❌ ${config?.method?.toUpperCase()} ${config?.url} - ${duration}ms`, error);
        }

        // Handle specific error cases
        if (response?.status === 401) {
          // Skip forced logout ONLY for pre-login credential actions (e.g. wrong password/OTP)
          const requestUrl = config?.url || '';
          const isPreLoginEndpoint =
            requestUrl.includes('/auth/login') ||
            requestUrl.includes('/auth/verify-login-otp') ||
            requestUrl.includes('/auth/send-otp') ||
            requestUrl.includes('/auth/verify-otp') ||
            requestUrl.includes('/auth/forgot-password') ||
            requestUrl.includes('/auth/confirm-code') ||
            requestUrl.includes('/auth/set-new-password') ||
            requestUrl.includes('/auth/check-user-exists') ||
            requestUrl.includes('/auth/linkedin/signin') ||
            requestUrl.includes('/public/');

          if (isPreLoginEndpoint) {
            // Skip forced logout for pre-login credential actions
            console.warn(`[Auth] 401 on ${requestUrl} — skipping forced logout (pre-login action)`);
          } else {
            // Session revoked or expired on authenticated endpoint
            if (!this.isHandling401) {
              this.isHandling401 = true;
              const sessionExpiredMsg = 'Session expired. Please log in again to continue.';

              try {
                sessionStorage.setItem('session_expired_toast', sessionExpiredMsg);
                localStorage.setItem('session_expired_toast', sessionExpiredMsg);
              } catch (e) {}

              // Immediately wipe tokens from storage to halt polling & loops
              try {
                localStorage.removeItem('token');
                localStorage.removeItem('authToken');
                localStorage.removeItem('auth_token');
                sessionStorage.removeItem('token');
                sessionStorage.removeItem('authToken');
                sessionStorage.removeItem('auth_token');
                Cookies.remove('authToken');
                Cookies.remove('token');
              } catch (e) {}

              toast.error(sessionExpiredMsg, {
                toastId: 'session_expired_toast',
                autoClose: 4000,
              });

              try {
                store.dispatch(logoutUser());
              } catch (logoutError) {
                console.error('Redux logout failed:', logoutError);
              }

              setTimeout(() => {
                this.isHandling401 = false;
                performEmergencyLogout();
              }, 800);
            }
          }
        } else if (response?.status === 403) {
          // Forbidden / Suspended
          const errData = response?.data;
          const isSuspended =
            errData?.is_suspended ??
            errData?.account_suspended ??
            errData?.data?.is_suspended;
          if (isSuspended) {
            store.dispatch(setSuspended(true));
          }
        } else if (response?.status === 429) {
          // Rate limiting
          // store.dispatch(addNotification({
          //   type: 'warning',
          //   message: 'Too many requests. Please wait a moment and try again.',
          // }));
        } else if (response?.status >= 500) {
          // Server error - attempt retry for GET requests
          if (config?.method === 'get' && !config._retry) {
            config._retry = true;
            await this.delay(1000); // Wait 1 second before retry
            return this.api.request(config);
          }

          // store.dispatch(addNotification({
          //   type: 'error',
          //   message: 'Server error. Please try again later.',
          // }));
        } else if (!response) {
          // Network error
          // store.dispatch(addNotification({
          //   type: 'error',
          //   message: 'Network error. Please check your connection.',
          // }));
        }

        return Promise.reject(error);
      }
    );
  }

  // Setup cache cleanup interval
  private setupCacheCleanup() {
    setInterval(() => {
      const now = Date.now();
      for (const [key, entry] of this.cache.entries()) {
        if (now > entry.expiry) {
          this.cache.delete(key);
        }
      }
    }, 5 * 60 * 1000); // Clean up every 5 minutes
  }

  // Generate unique request ID
  private generateRequestId(): string {
    return Math.random().toString(36).substring(2, 9);
  }

  // Delay utility for retries
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // Cache management
  private getCacheKey(url: string, params?: any): string {
    return `${url}${params ? JSON.stringify(params) : ''}`;
  }

  private setCache(key: string, data: any, ttl: number = 5 * 60 * 1000): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      expiry: Date.now() + ttl
    });
  }

  private getCache(key: string): any | null {
    const entry = this.cache.get(key);
    if (entry && Date.now() < entry.expiry) {
      return entry.data;
    }
    if (entry) {
      this.cache.delete(key);
    }
    return null;
  }

  private clearCache(pattern?: string): void {
    if (pattern) {
      for (const key of this.cache.keys()) {
        if (key.includes(pattern)) {
          this.cache.delete(key);
        }
      }
    } else {
      this.cache.clear();
    }
  }

  // Generic request method with caching and deduplication
  private async request<T>(config: AxiosRequestConfig & { cache?: boolean; cacheTTL?: number }): Promise<T> {
    const { cache = false, cacheTTL = 5 * 60 * 1000, ...axiosConfig } = config;
    const cacheKey = this.getCacheKey(axiosConfig.url || '', axiosConfig.params);

    // Check cache for GET requests
    if (axiosConfig.method === 'GET' && cache) {
      const cachedData = this.getCache(cacheKey);
      if (cachedData) {
        return cachedData;
      }
    }

    // Check for pending identical requests (deduplication)
    if (this.pendingRequests.has(cacheKey)) {
      return this.pendingRequests.get(cacheKey);
    }

    try {
      const requestPromise = this.api.request<T>(axiosConfig).then(response => response.data);

      // Store pending request
      this.pendingRequests.set(cacheKey, requestPromise);

      const result = await requestPromise;

      // Cache GET requests if caching is enabled
      if (axiosConfig.method === 'GET' && cache) {
        this.setCache(cacheKey, result, cacheTTL);
      }

      return result;
    } catch (error) {
      throw error;
    } finally {
      // Remove from pending requests
      this.pendingRequests.delete(cacheKey);
    }
  }

  // HTTP Methods with enhanced features
  async get<T>(url: string, config?: AxiosRequestConfig & { cache?: boolean; cacheTTL?: number }): Promise<T> {
    return this.request<T>({ ...config, method: 'GET', url, cache: config?.cache ?? true });
  }

  async post<T>(url: string, data?: any, config?: AxiosRequestConfig & { cache?: boolean; cacheTTL?: number }): Promise<T> {
    // Clear related cache entries on POST
    this.clearCache(url);
    return this.request<T>({ ...config, method: 'POST', url, data });
  }

  async put<T>(url: string, data?: any, config?: AxiosRequestConfig & { cache?: boolean; cacheTTL?: number }): Promise<T> {
    // Clear related cache entries on PUT
    this.clearCache(url);
    return this.request<T>({ ...config, method: 'PUT', url, data });
  }

  async patch<T>(url: string, data?: any, config?: AxiosRequestConfig & { cache?: boolean; cacheTTL?: number }): Promise<T> {
    // Clear related cache entries on PATCH
    this.clearCache(url);
    return this.request<T>({ ...config, method: 'PATCH', url, data });
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    // Clear related cache entries on DELETE
    this.clearCache(url);
    return this.request<T>({ ...config, method: 'DELETE', url });
  }

  // File upload method
  async uploadFile<T>(url: string, file: File, onUploadProgress?: (progress: number) => void): Promise<T> {
    const formData = new FormData();
    formData.append('file', file);

    return this.request<T>({
      method: 'POST',
      url,
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onUploadProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onUploadProgress(progress);
        }
      },
    });
  }




  // Batch requests
  async batch<T>(requests: AxiosRequestConfig[]): Promise<T[]> {
    try {
      const promises = requests.map(config => this.api.request<T>(config));
      const responses = await Promise.all(promises);
      return responses.map(response => response.data);
    } catch (error) {
      throw error;
    }
  }

  // Cancel request
  createCancelToken() {
    return axios.CancelToken.source();
  }

  // Check if error is cancel
  isCancel(error: any) {
    return axios.isCancel(error);
  }

  // Specific API Methods

  // Authentication
  async login(credentials: { email: string; password?: string; social_type?: string; full_name?: string; social_id?: string; profile_picture?: string; rememberMe?: boolean }): Promise<LoginResponse> {
    const { rememberMe, ...payload } = credentials;
    return this.post<LoginResponse>('/auth/login', payload);
  }

  async verifyLoginOtp(email: string, otp: string): Promise<LoginResponse> {
    return this.post<LoginResponse>('/auth/verify-login-otp', { email, otp });
  }

  // Candidates
  async getApplicantsList(params: { limit: number; page: number; search: string | null; filters: any }): Promise<CandidateListResponse> {
    return this.post<CandidateListResponse>('/users/get-applicants', params);
  }
    async getCandidateList(params: { limit: number; page: number; search: string | null; filters: any }): Promise<CandidateListResponse> {
    return this.post<CandidateListResponse>('/users/get-candidatelist', params);
  }
  async getBlockList(): Promise<any> {
    return this.get<any>('/settings/block-list', { cache: false });
  }
  // BEFORE (Assuming 'type' is not defined, which is likely a placeholder/error in the original code)
  // async getUserConnect(params: { limit: number; page: number; search:string, type: type ?? "Matched", }): Promise<CandidateListResponse> {
  //     return this.post<CandidateListResponse>('/users/get-user-connections', params);
  // }

  // AFTER (Assuming 'type' is supposed to be 'CandidateType')
  // Inside apiService
  async getUserConnect(params: {
    limit: number;
    page: number;
    search: string | null;
    type: "Liked" | "Matched" | "Requests" | "follow";
    filters: any;
    includeUnmatched?: boolean;
  }): Promise<CandidateListData> {
    return this.post<CandidateListData>(
      "/users/get-user-connections",
      params
    );
  }

  async getSettings(): Promise<any> {
    return this.get<any>('/users/get-setting-process', { cache: false });
  }

  async getActiveDevices(): Promise<any> {
    return this.get<any>('/auth/active-devices', { cache: false });
  }

  async revokeDevice(sessionId: number | string): Promise<any> {
    return this.post<any>('/auth/revoke-device', { session_id: sessionId });
  }

  async getNotifications(
    params: {
      page_number?: number;
      page_size?: number;
      user_type?: 'company' | 'user';
      type?: string;
      category?: string;
    } = {},
  ): Promise<any> {
    const filterType = params.type || params.category;
    return this.post<any>(
      '/notification/get',
      {
        page_number: params.page_number ?? 1,
        page_size: params.page_size ?? 20,
        user_type: params.user_type ?? 'company',
        ...(filterType && filterType !== 'all' && filterType !== 'All'
          ? { type: filterType.toLowerCase() }
          : {}),
      },
      { cache: false }
    );
  }

  async markNotificationRead(notificationId: number | string = 0): Promise<any> {
    return this.post<any>(
      '/notification/read',
      { notification_id: notificationId },
      { cache: false }
    );
  }

  async getLookigFor(params?: {
    tab?: string;
    status?: string;
    is_draft?: boolean;
    is_closed?: boolean;
    search?: string;
    page_number?: number;
    page_size?: number;
  }): Promise<any> {
    return this.get<any>('/settings/get-looking-for', { params, cache: false });
  }

  async getLookingForById(jobId: number | string): Promise<any> {
    return this.get<any>(`/settings/looking-for/${jobId}`, { cache: false });
  }

  async getIndustriesByDesignation(designationId: number | string): Promise<any> {
    return this.get<any>('/common/get-industries-by-designation', {
      params: { designation_id: designationId },
      cache: true,
      cacheTTL: 10 * 60 * 1000, // Cache for 10 minutes
    });
  }

  async getSkillsByIndustries(industryIds: number[] | string, page: number = 1, pageSize: number = 10): Promise<any> {
    const ids = Array.isArray(industryIds) ? industryIds.join(',') : industryIds;
    return this.get<any>('/common/get-skills-by-industries', {
      params: { industry_ids: ids, page, page_size: pageSize },
      cache: true,
      cacheTTL: 10 * 60 * 1000, // Cache for 10 minutes
    });
  }
  async updateSettings(data: any): Promise<any> {
    return this.post<any>('/settings/update-settings', data);
  }

  async addlocation(data: any): Promise<any> {
    return this.post<any>('/settings/add-location', data);
  }
  async getLookingFor(): Promise<CandidateListResponse> {
    return this.get<CandidateListResponse>('/users/get-lookings-for', {
      cache: false,
    });
  }

  async addLookingFor(data: any): Promise<any> {
    const normalizedApplicationDeadline = this.normalizeDateOnly(data.application_deadline);
    const payload: any = {
      ...data,
      is_closed: data.isClosed ?? data.is_closed ?? false,
      is_draft: data.isDraft ?? data.is_draft ?? true,
      // Explicit mapping for new fields
      designation_id: data.yourDesignationId || data.designation_id,
      job_title: data.job_title || data.designation || data.yourDesignation,
      skills: Array.isArray(data.skills)
        ? data.skills
        : Array.isArray(data.selectedSkills)
        ? data.selectedSkills.map((s: any) => s?.name || s?.id || s)
        : [],
      employment_type_ids: data.employment_type_ids || [], 
      work_experience_years: data.work_experience_years ?? 0,
      work_experience_months: data.work_experience_months ?? 0,
      experience_id: data.experience_id || data.workExperience,
      salary_min: data.salary_min ?? null,
      salary_max: data.salary_max ?? null,
      salary_currency: data.salary_currency || 'INR',
      salary_period: data.salary_period || 'year',
      openings_count: data.openings_count && data.openings_count >= 1 ? data.openings_count : 1,
      location_text: data.location_text || '',
      industry_tags: data.industry_tags || [],
      responsibilities: data.responsibilities || [],
      requirements: data.requirements || [],
      application_deadline: normalizedApplicationDeadline,
    };

    if (Object.prototype.hasOwnProperty.call(data, 'benefits')) {
      payload.benefits = data.benefits;
    }

    if (data.id != null) {
      payload.id = data.id;
    }

    return this.post<any>('/settings/add-lookings-for', payload);
  }
 async addDuplicateLookingFor(data: any): Promise<any> {
    const normalizedApplicationDeadline = this.normalizeDateOnly(data.application_deadline);
    const payload: any = {
      ...data,
      is_closed: data.isClosed ?? data.is_closed ?? false,
      is_draft: data.isDraft ?? data.is_draft ?? true,
      // Explicit mapping for new fields
      designation_id: data.yourDesignationId || data.designation_id,
      job_title: data.job_title || data.designation || data.yourDesignation,
      skills: Array.isArray(data.skills)
        ? data.skills
        : Array.isArray(data.selectedSkills)
        ? data.selectedSkills.map((s: any) => s?.name || s?.id || s)
        : [],
      employment_type_ids: data.employment_type_ids || [], 
      work_experience_years: data.work_experience_years ?? 0,
      work_experience_months: data.work_experience_months ?? 0,
      experience_id: data.experience_id || data.workExperience,
      salary_min: data.salary_min ?? null,
      salary_max: data.salary_max ?? null,
      salary_currency: data.salary_currency || 'INR',
      salary_period: data.salary_period || 'year',
      openings_count: data.openings_count && data.openings_count >= 1 ? data.openings_count : 1,
      location_text: data.location_text || '',
      industry_tags: data.industry_tags || [],
      responsibilities: data.responsibilities || [],
      requirements: data.requirements || [],
      application_deadline: normalizedApplicationDeadline,
    };

    if (Object.prototype.hasOwnProperty.call(data, 'benefits')) {
      payload.benefits = data.benefits;
    }

    if (data.id != null) {
      payload.id = data.id;
    }

    return this.post<any>('/settings/add-lookings-for', payload);
  }
  async addLookingForWithStep(
  data: any,
  step: 'basics' | 'preferences' | 'description' | 'communication'
): Promise<any> {
  const normalizedApplicationDeadline = this.normalizeDateOnly(data.application_deadline);

  // 🚨 Safety check
  if (!data.id && step !== 'basics') {
    throw new Error('Job ID missing. Complete basics step first.');
  }

  let payload: any = {
    id: data.id ?? undefined,
    is_closed: data.isClosed ?? data.is_closed ?? false,
    is_draft: data.is_draft !== undefined ? Boolean(data.is_draft) : true,
  };

  // ───────────── STEP 1: BASICS ─────────────
  if (step === 'basics') {
    payload = {
      ...payload,
      designation_id: data.designation_id,
      job_title: data.job_title,
      employment_type_ids: data.employment_type_ids || [],
      industry_tags: data.industry_tags || [],
      work_mode: data.work_mode || '',
      experience_id: data.experience_id,
      work_experience_years: data.work_experience_years ?? 0,
      work_experience_months: data.work_experience_months ?? 0,

      salary_min: data.salary_min ?? null,
      salary_max: data.salary_max ?? null,
      salary_currency: data.salary_currency || 'INR',
      salary_period: data.salary_period || 'year',
      salary_visibility: data.salary_visibility || 'range',

      openings_count: data.openings_count ?? 1,

      location_text: data.location_text || '',
      location_lat: data.location_lat ?? null,
      location_lng: data.location_lng ?? null,
      application_deadline: normalizedApplicationDeadline,
    
    };
  }

  // ───────────── STEP 2: PREFERENCES ─────────────
  if (step === 'preferences') {
    payload = {
      ...payload,
      skills: Array.isArray(data.skills)
        ? data.skills
        : Array.isArray(data.selectedSkills)
        ? data.selectedSkills.map((s: any) => s?.name || s?.id || s)
        : [],

      qualification: data.qualification || [],
      gender_preference:
        typeof data.gender_preference === 'string'
          ? data.gender_preference
          : Array.isArray(data.gender_preference)
            ? data.gender_preference[0] || 'Any'
            : 'Any',

      language_requirements: data.language_requirements || [],
      willingness_to_relocate: data.willingness_to_relocate || 'negotiable',

      age_min: data.age_min ?? 18,
      age_max: data.age_max ?? 75,
    };
  }

  // ───────────── STEP 3: DESCRIPTION ─────────────
  if (step === 'description') {
    payload = {
      ...payload,
      description: data.description || '',
      responsibilities: data.responsibilities || [],
      requirements: data.requirements || [],

      benefits: data.benefits ?? null,
      about_company: data.about_company || '',
      perks: data.perks || [],
      careers_page_link: data.careers_page_link || '',
    };
  }

  // ───────────── STEP 4: COMMUNICATION ─────────────
  if (step === 'communication') {
    payload = {
      ...payload,
      notify_email: data.notify_email ?? true,
      notify_sms: data.notify_sms ?? false,
      notify_whatsapp: data.notify_whatsapp ?? true,
      notify_emails: data.notify_emails || [],
      ack_template: data.ack_template || '',
    };
  }
if (step !== 'basics') {
  delete payload.application_deadline;
}
  // ✅ FINAL ENDPOINT
  const endpoint = `/onboarding/job-step/${step}`;

  return this.post<any>(endpoint, payload);
}

  async deleteJob(jobId: string | number): Promise<any> {
    return this.post<any>('/onboarding/delete-job', { job_id: jobId });
  }

  async bulkPublishJobs(jobIds: (number | string)[]): Promise<any> {
    const numericIds = jobIds.map((id) => Number(id)).filter((id) => !isNaN(id) && id > 0);
    return this.post<any>('/settings/bulk-publish-jobs', { job_ids: numericIds });
  }

  async bulkDraftJobs(jobIds: (number | string)[]): Promise<any> {
    const numericIds = jobIds.map((id) => Number(id)).filter((id) => !isNaN(id) && id > 0);
    return this.post<any>('/settings/bulk-draft-jobs', { job_ids: numericIds });
  }

  async bulkCloseJobs(jobIds: (number | string)[]): Promise<any> {
    const numericIds = jobIds.map((id) => Number(id)).filter((id) => !isNaN(id) && id > 0);
    return this.post<any>('/settings/bulk-close-jobs', { job_ids: numericIds });
  }

  async bulkDeleteJobs(jobIds: (number | string)[]): Promise<any> {
    const numericIds = jobIds.map((id) => Number(id)).filter((id) => !isNaN(id) && id > 0);
    return this.post<any>('/settings/bulk-delete-jobs', { job_ids: numericIds });
  }

  // Email Check
  async checkUserExists(email: string): Promise<ApiResponse<{
    exists: boolean;
    user_id: number | null;
    name: string | null;
    profile_completed: boolean;
  }>> {
    return this.post('/auth/check-user-exists', { email });
  }

  // Company duplicate check (real-time on the company details form)
  async checkCompanyExists(data: {
    company_name?: string;
    company_website?: string;
    company_id?: number;
    is_manual_company?: boolean;
  }): Promise<ApiResponse<{
    exists: boolean;
    company_name: string | null;
    name_matched?: boolean;
    website_matched?: boolean;
    website_mismatch?: boolean;
    duplicate_type?: 'both' | 'name' | 'website' | null;
    action_url?: string | null;
    registered_company_name?: string | null;
    registered_website?: string | null;
  }>> {
    return this.post<ApiResponse<{
      exists: boolean;
      company_name: string | null;
      name_matched?: boolean;
      website_matched?: boolean;
      website_mismatch?: boolean;
      duplicate_type?: 'both' | 'name' | 'website' | null;
      action_url?: string | null;
      registered_company_name?: string | null;
      registered_website?: string | null;
    }>>('/onboarding/check-company-exists', data);
  }

  // Real-time domain–company name match verification
  async verifyDomainMatch(data: {
    company_name: string;
    company_website: string;
    company_email?: string;
  }): Promise<ApiResponse<{
    matched: boolean;
    score: number;
    status?: 'verified' | 'likely' | 'uncertain' | 'mismatch';
    confidence?: 'high' | 'medium' | 'low';
    title: string;
    site_name: string;
    meta_description: string;
    reasons?: string[];
    error: string | null;
  }>> {
    return this.post<ApiResponse<{
      matched: boolean;
      score: number;
      status?: 'verified' | 'likely' | 'uncertain' | 'mismatch';
      confidence?: 'high' | 'medium' | 'low';
      title: string;
      site_name: string;
      meta_description: string;
      reasons?: string[];
      error: string | null;
    }>>('/onboarding/verify-domain-match', data);
  }

  // Onboarding
  async submitBasicDetails(data: any): Promise<BasicDetailsResponse> {
    const response = await this.post<BasicDetailsResponse>('/onboarding/basic-details', data);
    this.clearProgressCache();
    return response;
  }

  async submitCompanyDetails(data: any): Promise<CompanyDetailsResponse> {
    const response = await this.post<CompanyDetailsResponse>('/onboarding/company-details', data);
    this.clearProgressCache();
    return response;
  }

  async submitLookingFor(data: any): Promise<LookingForResponse> {
    const response = await this.post<LookingForResponse>('/onboarding/looking-for', data);
    this.clearProgressCache();
    return response;
  }

  async addCompanyPitchVideo(data: any): Promise<ApiResponse> {
    const response = await this.post<ApiResponse>('/onboarding/add-company-pitch-video', data);
    this.clearProgressCache();
    return response;
  }
  async completeVerificationStep(): Promise<ApiResponse> {
    const response = await this.post<ApiResponse>('/onboarding/complete-verification-step', {});
    this.clearProgressCache();
    return response;
  }
  async getCompanySizesRange(): Promise<ApiResponse> {
    return this.get<ApiResponse>('/common/get-company-size-ranges');
  }
  async generateDomainFile(): Promise<ApiResponse>{
    return this.get<ApiResponse>('/onboarding/domain-verification-code');
  }

  // Progress tracking
  async getSignupProgress(): Promise<OnboardingProgressResponse> {
    try {
      return await this.get<OnboardingProgressResponse>('/onboarding/progress', {
        cache: true,
        cacheTTL: 2 * 60 * 1000 // Cache for 2 minutes to balance freshness with performance
      });
    } catch (error: any) {
      // Enhanced error handling for progress API
      if (error.response?.status === 404) {
        // User not found or no progress data
        throw new Error('User progress not found. Please ensure you are logged in.');
      } else if (error.response?.status === 403) {
        // Forbidden - user doesn't have access
        throw new Error('Access denied. Please check your permissions.');
      } else if (error.response?.status >= 500) {
        // Server error
        throw new Error('Unable to retrieve progress. Please try again later.');
      } else if (!error.response) {
        // Network error
        throw new Error('Network error. Please check your connection and try again.');
      }

      // Re-throw original error for other cases
      throw error;
    }
  }

  // Clear progress cache when step is completed or updated
  clearProgressCache(): void {
    this.clearCache('/onboarding/progress');
  }

  // Get step data for form pre-filling
  async getStepData(stepId: number): Promise<ApiResponse<any>> {
    try {
      return await this.get<ApiResponse<any>>(`/onboarding/step/${stepId}`, {
        cache: false,
        // cacheTTL: 5 * 60 * 1000 // Cache step data for 5 minutes
      });
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error('Step data not found.');
      } else if (error.response?.status === 403) {
        throw new Error('Access denied to step data.');
      } else if (error.response?.status >= 500) {
        throw new Error('Unable to retrieve step data. Please try again later.');
      } else if (!error.response) {
        throw new Error('Network error. Please check your connection and try again.');
      }
      throw error;
    }
  }

  // Update progress when step is completed
  async updateStepProgress(stepId: number, statusId: number, stepData?: any): Promise<ApiResponse<any>> {
    try {
      const result = await this.post<ApiResponse<any>>('/onboarding/update', {
        step_id: stepId,
        status_id: statusId,
        step_data: stepData
      });

      // Clear progress cache after successful update
      this.clearProgressCache();

      return result;
    } catch (error: any) {
      if (error.response?.status === 400) {
        throw new Error('Invalid step transition or data.');
      } else if (error.response?.status === 403) {
        throw new Error('Access denied to update progress.');
      } else if (error.response?.status >= 500) {
        throw new Error('Unable to update progress. Please try again later.');
      } else if (!error.response) {
        throw new Error('Network error. Please check your connection and try again.');
      }
      throw error;
    }
  }



  // Common data with enhanced caching
  async getDesignations(search?: string): Promise<DesignationsResponse> {
    const url = search ? `common/get-designation?search=${encodeURIComponent(search)}` : 'common/get-designation';
    return this.get<DesignationsResponse>(url, {
      cache: true,
      cacheTTL: search ? 2 * 60 * 1000 : 10 * 60 * 1000 // Cache search results for 2 min, full list for 10 min
    });
  }

  async getWorkExperience(): Promise<WorkExperienceResponse> {
    return this.get<WorkExperienceResponse>('common/get-experience', {
      cache: true,
      cacheTTL: 30 * 60 * 1000 // Cache for 30 minutes
    });
  }

  async getJobFormOptions(): Promise<any> {
    return this.get<any>('common/get-job-form-options', {
      cache: true,
      cacheTTL: 30 * 60 * 1000
    });
  }

  

  async getOnboardingCurrencies(): Promise<any> {
    return this.get<any>('/onboarding/master/currencies', {
      cache: false,
      cacheTTL: 30 * 60 * 1000
    });
  }

  async getSkills(search?: string): Promise<SkillsResponse> {
    const url = search ? `common/get-skills?search=${encodeURIComponent(search)}` : 'common/get-skills';
    return this.get<SkillsResponse>(url, {
      cache: true,
      cacheTTL: 15 * 60 * 1000 // Cache for 15 minutes
    });
  }

  async getIndustries(search?: string): Promise<any> {
    const url = search
      ? `common/get-industries?search=${encodeURIComponent(search)}`
      : 'common/get-industries';
    return this.get<any>(url, {
      cache: false // Keep options fresh from DB
    });
  }

  async getDomainExpertise(params?: { search?: string; industryIds?: number[] }): Promise<any> {
    const queryParts: string[] = [];
    const industryIds = params?.industryIds;

    if (params?.search && params.search.trim().length > 0) {
      queryParts.push(`search=${encodeURIComponent(params.search.trim())}`);
    }

    if (Array.isArray(industryIds) && industryIds.length > 0) {
      queryParts.push(
        `industry_ids=${encodeURIComponent(industryIds.join(','))}`,
      );
    }

    const queryString = queryParts.length > 0 ? `?${queryParts.join('&')}` : '';
    const url = `common/get-domain-expertise${queryString}`;

    return this.get<any>(url, {
      cache: false // Keep options fresh from DB
    });
  }

  async getSkillSuggestions(params?: {
    search?: string;
    industryIds?: number[];
    domainIds?: number[];
    designationId?: number;
    limit?: number;
  }): Promise<any> {
    const queryParts: string[] = [];
    const industryIds = params?.industryIds;
    const domainIds = params?.domainIds;
    const designationId = params?.designationId;

    if (params?.search && params.search.trim().length > 0) {
      queryParts.push(`search=${encodeURIComponent(params.search.trim())}`);
    }

    if (Array.isArray(industryIds) && industryIds.length > 0) {
      queryParts.push(
        `industry_ids=${encodeURIComponent(industryIds.join(','))}`,
      );
    }

    if (Array.isArray(domainIds) && domainIds.length > 0) {
      queryParts.push(`domain_ids=${encodeURIComponent(domainIds.join(','))}`);
    }

    if (Number.isFinite(designationId) && Number(designationId) > 0) {
      queryParts.push(`designation_id=${Math.floor(Number(designationId))}`);
    }

    if (params?.limit && Number.isFinite(params.limit) && params.limit > 0) {
      queryParts.push(`limit=${Math.floor(params.limit)}`);
    }

    const queryString = queryParts.length > 0 ? `?${queryParts.join('&')}` : '';
    const url = `common/get-skill-suggestions${queryString}`;

    return this.get<any>(url, {
      cache: false
    });
  }

  async getSuggestedSkillsByDesignation(designationId: number): Promise<SkillsResponse> {
    return this.get<SkillsResponse>(`common/get-suggested-skills?designation_id=${designationId}`, {
      cache: true,
      cacheTTL: 30 * 60 * 1000 // Cache for 30 minutes
    });
  }

  async getCompanies(search?: string): Promise<CompaniesResponse> {
    const url = search ? `profile/get-companies?search=${encodeURIComponent(search)}` : 'profile/get-companies';
    return this.get<CompaniesResponse>(url, {
      cache: true,
      cacheTTL: search ? 2 * 60 * 1000 : 10 * 60 * 1000
    });
  }

  // Credits
  async getCreditBalance(): Promise<CreditBalanceResponse> {
    const storeState = (store as any)?.getState?.()?.credits?.balance || null;
    return Promise.resolve({
      status: true,
      message: 'Credit balance from store',
      data: storeState || {
        totalCredits: 0,
        availableCredits: 0,
        reservedCredits: 0,
        monthlyFreeCredits: 0,
        creditsUsedThisMonth: 0,
        lifetimeCreditsEarned: 0,
        lifetimeCreditsSpent: 0,
        lastUpdated: new Date().toISOString(),
        expiringCredits: [],
      }
    } as CreditBalanceResponse);
  }

  async getDashboardData(period?: PitchAnalyticsPeriod): Promise<DashboardDataResponse> {
    const body = period ? { key: period } : {};
    return this.post<DashboardDataResponse>('/dashboard/analytics', body, {
      cache: false // Dashboard data should be fresh
    });
  }

  async getCreditFeatures(): Promise<CreditFeaturesResponse> {
    return this.get<CreditFeaturesResponse>('/credits/features');
  }

  async addCredits(data: any): Promise<ApiResponse<any>> {
    return this.post<ApiResponse<any>>('/onboarding/credits', data);
  }

  // ── Onboarding verification stubs (wired to backend when endpoints are ready) ──

  async sendEmailOTP(email: string,resend?: boolean): Promise<SendOTPResponse> {
    return this.post<SendOTPResponse>('/auth/send-otp', { email,resend });
  }

  async verifyEmailOTP(email: string, otp: string): Promise<VerifyOTPResponse> {
    return this.post<VerifyOTPResponse>('/auth/verify-otp', { email, otp });
  }

  async verifyDomain(domain: string): Promise<DomainVerificationResponse> {
    const response = await this.post<DomainVerificationResponse>('/onboarding/verify-domain', { domain });
    this.clearProgressCache();
    return response;
  }

  async updateDomain(domain: string, company_name?: string, company_id?: number | null, is_manual_company?: boolean): Promise<any> {
    const response = await this.post<any>('/onboarding/update-domain', { domain, company_name, company_id, is_manual_company });
    this.clearProgressCache();
    return response;
  }
// 1. Send GSTIN verification as clean JSON
async submitGSTVerification(gstin: string): Promise<GSTVerificationResponse> {
  const response = await this.post<GSTVerificationResponse>('/onboarding/verify-gst', { gstin });
  this.clearProgressCache();
  return response;
}

// 2. Upload Certificate as multipart form data
async uploadGstCertificates(certificate: File, userId?: number | string): Promise<GSTVerificationResponse> {
  const s3Folder = getCompanyS3Folder('gst-certificate', userId);
  const fd = new FormData();
  fd.append('certificate', certificate);
  fd.append('folder', s3Folder);

  return this.post<GSTVerificationResponse>(
    '/onboarding/upload-gst-certificate',
    fd,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  );
}

// 3. Delete GST Certificate
async deleteGstCertificate(): Promise<GSTVerificationResponse> {
  const response = await this.delete<GSTVerificationResponse>('/onboarding/delete-gst-certificate');
  this.clearProgressCache();
  return response;
}

  async createVerificationOrderRazorpay(): Promise<VerificationOrderRazorpayResponse> {
    return this.post<VerificationOrderRazorpayResponse>('/onboarding/payment-verification/razorpay/create-order', {});
  }

  async verifyVerificationPaymentRazorpay(data: {
    razorpay_order_id: string;
    razorpay_payment_id: string;
    razorpay_signature: string;
  }): Promise<VerifyVerificationPaymentResponse> {
    const response = await this.post<VerifyVerificationPaymentResponse>('/onboarding/payment-verification/razorpay/verify', data);
    this.clearProgressCache();
    return response;
  }

  async createVerificationIntentStripe(): Promise<VerificationIntentStripeResponse> {
    return this.post<VerificationIntentStripeResponse>('/onboarding/payment-verification/stripe/create-intent', {});
  }

  async verifyVerificationPaymentStripe(paymentIntentId: string): Promise<VerifyVerificationPaymentResponse> {
    const response = await this.post<VerifyVerificationPaymentResponse>('/onboarding/payment-verification/stripe/verify', { payment_intent_id: paymentIntentId });
    this.clearProgressCache();
    return response;
  }

  async getTransactionHistory(page: number = 1, limit: number = 10, startDate?: string, endDate?: string): Promise<ApiResponse<any>> {
    let url = `/credits/get-transaction-history?page=${page}&limit=${limit}`;
    if (startDate) url += `&start_date=${startDate}`;
    if (endDate) url += `&end_date=${endDate}`;
    return this.get<ApiResponse<any>>(url, {
      cache: false // Don't cache transaction history for real-time updates
    });
  }

  async getCreditUsageHistory(page: number = 1, limit: number = 20, startDate?: string, endDate?: string): Promise<ApiResponse<any>> {
    let url = `/credits/usage-history?page=${page}&limit=${limit}`;
    if (startDate) url += `&start_date=${startDate}`;
    if (endDate) url += `&end_date=${endDate}`;
    return this.get<ApiResponse<any>>(url, {
      cache: false // Don't cache usage history for real-time updates
    });
  }

  // Stripe Payments
  async getStripeConfig(): Promise<ApiResponse<{
    publishableKey: string;
    supportedCurrencies: string[];
    supportedPaymentMethods: string[];
    pricing?: {
      usd: number;
      inr: number;
    };
  }>> {
    return this.get<ApiResponse<any>>('/payments/config');
  }

  async getCreditPricingConfig(): Promise<ApiResponse<Array<{
    config_key: string;
    config_value: string | number;
    currency: string;
  }>>> {
    return this.get<ApiResponse<any>>('/payments/pricing-config');
  }

  async getCreditPackages(currency: string = 'usd'): Promise<ApiResponse<{
    packages: any[];
    currency: string;
  }>> {
    return this.get<ApiResponse<any>>(`/payments/packages?currency=${currency}`);
  }

  async createPaymentIntent(data: {
    package_id: number;
    currency?: string;
    promo_code?: string;
    custom_credits?: number;
    custom_amount?: number;
  }): Promise<ApiResponse<{
    clientSecret: string;
    paymentIntentId: string;
    purchaseNumber: string;
    amount: number;
    currency: string;
    package: any;
  }>> {
    return this.post<ApiResponse<any>>('/payments/create-payment-intent', data);
  }

  async confirmPayment(paymentIntentId: string): Promise<ApiResponse<{
    purchase: any;
    newBalance: number;
  }>> {
    return this.post<ApiResponse<any>>('/payments/confirm-payment', {
      payment_intent_id: paymentIntentId
    });
  }

  async createRazorpayOrder(data: {
    package_id: number;
    currency?: string;
    promo_code?: string;
    custom_credits?: number;
    custom_amount?: number;
  }): Promise<ApiResponse<{
    order_id: string;
    amount: number;
    currency: string;
    key_id: string;
    package: any;
  }>> {
    return this.post<ApiResponse<any>>('/payments/razorpay/create-order', data);
  }

  async verifyRazorpayPayment(data: {
    razorpay_order_id: string;
    razorpay_payment_id: string;
    razorpay_signature: string;
  }): Promise<ApiResponse<{
    purchase: any;
    newBalance: number;
  }>> {
    return this.post<ApiResponse<any>>('/payments/razorpay/verify-payment', data);
  }

  async getPaymentMethods(): Promise<ApiResponse<{
    paymentMethods: any[];
  }>> {
    return this.get<ApiResponse<any>>('/payments/payment-methods');
  }

  async getPurchaseHistory(page: number = 1, limit: number = 10): Promise<ApiResponse<{
    purchases: any[];
    pagination: any;
  }>> {
    return this.get<ApiResponse<any>>(`/payments/purchase-history?page=${page}&limit=${limit}`);
  }

  // Profile
  async getProfile(): Promise<ProfileResponse> {
    return this.get<ProfileResponse>('/profile/get-profile', {
      cache: false // Keep profile view real-time
    });
  }
  async getPersonalProfile(): Promise<any> {
    return this.get<any>('/profile/get-personal-details', {
      cache: false
    });
  }
  async getPerksAndBenefits(): Promise<ProfileResponse> {
    return this.get<ProfileResponse>('/profile/get-perks-list', {
      cache: false // Keep profile view real-time
    });
  }
  async getCurrentUserForChat(): Promise<ProfileResponse> {
    return this.get<ProfileResponse>('/profile/get-profile', {
      cache: false // Don't cache for chat to ensure real-time data
    });
  }

  async getSocialLinks(): Promise<any> {
    return this.get<any>('profile/get-social-link', { cache: false });
  }
  async addSocialLinks(data:any): Promise<any> {
    return this.post<any>('profile/add-social-link', data);
  }
  async deleteSocialLinks(data:any): Promise<any> {
    return this.post<any>('profile/delete-social-link', data);
  }
    async getDays(): Promise<any> {
    return this.get<any>('/profile/getDays', {
      cache: false // Don't cache for chat to ensure real-time data
    });
  }

    async getReviews(): Promise<any> {
    return this.get<any>('/profile/getReviewsList', {
      cache: false // Don't cache for chat to ensure real-time data
    });
  }

  async editProfile(data: any): Promise<ProfileResponse> {
    return this.post<ProfileResponse>('/profile/update-profile', data);
  }

  async updatePersonalInfo(data: { full_name?: string; name?: string; phone?: string }): Promise<any> {
    return this.post<any>('/profile/update-personal-info', data);
  }

  async getProfileMedia(data: any): Promise<any> {
    return this.post<any>('/profile/getMedia', data);
  }
  // apiService.ts
  async sendConnectRequest(payload: { other_user_id: number; message: string; like?: number; type?: "user" | "suite" | "company" }): Promise<ConnectResponse> {
    return this.post<ConnectResponse>("/users/send-connect-request", payload);
  }

  async acceptConnectRequest(payload: { other_user_id: number; message?: string; like?: number; type?: "user" | "suite" | "company" }): Promise<ConnectResponse> {
    return this.post<ConnectResponse>("/users/accept-connect-request", payload);
  }

  async removeRequest(payload: RemoveRequestPayload): Promise<RemoveRequestResponse> {
    return this.post<RemoveRequestResponse>("/users/remove-like", payload);
  }
  async unBlockUser(payload: { unblock_user_id: number }): Promise<any> {
    return this.post<any>("/settings/un-block", payload);
  }

  async blockUser(payload: { block_user_id: number }): Promise<any> {
    return this.post<any>("/settings/block-user", payload);
  }


  // Get Match List
  async getMatchList(): Promise<any> {
    return this.get('/profile/get-match-list', {});
  }



  // async getProjects(): Promise<any> {
  //   return this.get("/profile/get-project");
  // }


  // async addProject(body: any): Promise<PortfolioResponse> {
  //   return this.post<PortfolioResponse>("/profile/add-project", body);
  // }

  async getProjects(): Promise<any> {
    return this.get("/profile/get-project" , {
      cache: false // Keep profile view real-time
    });
  }
  async getCategories(): Promise<any> {
    return this.get("/profile/getCategories", {
      cache: true,
      cacheTTL: 15 * 60 * 1000, // Cache for 15 minutes — static-ish list
    });
  }

  async addProject(payload: any): Promise<any> {
    return this.post("/profile/add-project", payload);
  }



 async savePitchVideo(payload: any): Promise<any> {

    return this.post("/profile/update-pitch", payload);
  }
 async deletePitchVideo(payload: any): Promise<any> {

    return this.post("/profile/delete-pitch", payload);
  }
 async activePitchVideo(payload: any): Promise<any> {

    return this.post("/profile/update-active-pitch", payload);
  }


  async emailVerify(payload: any): Promise<any> {
    return this.post("/auth/forgot-password", payload);
  }

  async ConfirmCode(payload: any): Promise<any> {
    return this.post("/auth/confirm-code", payload);
  }

  async setNewPassword(payload: { user_id: string; new_password: string }): Promise<any> {
    return this.post("/auth/set-new-password", payload);
  }






  async removeProject(id: any): Promise<any> {


    return this.get(`/profile/delete-project?id=${id}`);
  }

  // Chat
  async getAllNewMatches(): Promise<ChatAllMatched> {
    return this.post<ChatAllMatched>('chat/get-match-list');
  }

  async startChart(data: any): Promise<any> {
    const payload = typeof data === 'string' ? JSON.parse(data) : data;
    return this.post<any>('chat/start-chat', payload);
  }

  async sendChatMessageNotification(payload: {
    receiver_id: number;
    message: string;
    job_id?: number | null;
    job_title?: string | null;
  }): Promise<any> {
    return this.post<any>('chat/send-notification', payload);
  }

  async uploadChatFile(
    file: File,
    folder: string,
    onProgress?: (progress: number) => void,
  ): Promise<{ url: string; name: string; mimetype: string; size: number }> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', folder);
    const response = await this.api.post<{ status: boolean; data: { url: string; name: string; mimetype: string; size: number }; message: string }>(
      '/upload/chat-file',
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: onProgress
          ? (e) => { if (e.total) onProgress(Math.round((e.loaded * 100) / e.total)); }
          : undefined,
      },
    );
    if (!response.data?.status) throw new Error(response.data?.message || 'Upload failed');
    return response.data.data;
  }
  async addFeedback(data: any): Promise<any> {
    return this.post<any>('chat/rate-users', data);
  }
  async getUserRating(data: any): Promise<any> {
    return this.post<any>('chat/get-user-rating', data);
  }

  async blockChat(data: any): Promise<any> {
    return this.post<any>('chat/block', data);
  }
async getReportReasonList(): Promise<any> {
    return this.get<any>('chat/report-reasons', {});
  }
  
  async chatReport(data: any): Promise<any> {
    return this.post<any>('chat/report', data);
  }
  async getBlockStatus(data: any): Promise<any> {
    return this.post('chat/get-block-status', data);
  }
  async unMatchChat(data: any): Promise<any> {
    return this.post<any>('chat/remove-match', data);
  }

  async verifyMatchBatch(otherUserIds: number[]): Promise<ApiResponse<{ matched_ids: number[]; unmatched_ids: number[] }>> {
    return this.post<ApiResponse<{ matched_ids: number[]; unmatched_ids: number[] }>>('/chat/verify-match-batch', { other_user_ids: otherUserIds });
  }

  async addShortlist(data: { other_user_id: number; job_position_id: number; reason?: string }): Promise<any> {
    return this.post<any>('users/add-shortlist', data);
  }

  async addScheduledInterview(data: { other_user_id: number; job_position_id: number; reason?: string }): Promise<any> {
    return this.post<any>('users/add-scheduled-Interview', data);
  }

  async addInterviewed(data: { other_user_id: number; job_position_id: number; reason?: string }): Promise<any> {
    return this.post<any>('users/add-interviewed', data);
  }

  async addHired(data: { other_user_id: number; job_position_id: number; reason?: string }): Promise<any> {
    return this.post<any>('users/add-Hired', data);
  }

  async addNotHired(data: { other_user_id: number; job_position_id: number; reason?: string }): Promise<any> {
    return this.post<any>('users/add-not-hired', data);
  }

   async toggleJobStatus(jobId: number, data: { action: string } ): Promise<any> {
    return this.patch<any>(`settings/looking-for/${jobId}/status`, data);
  }
  // Utility methods

  // Cancel all pending requests
  cancelAllRequests(): void {
    for (const [key, cancelToken] of this.cancelTokens.entries()) {
      cancelToken.cancel('Request cancelled by user');
      this.cancelTokens.delete(key);
    }
    this.pendingRequests.clear();
  }

  // Cancel specific request
  cancelRequest(requestId: string): void {
    const cancelToken = this.cancelTokens.get(requestId);
    if (cancelToken) {
      cancelToken.cancel('Request cancelled by user');
      this.cancelTokens.delete(requestId);
    }
  }

  // Clear all cache
  clearAllCache(): void {
    this.clearCache();
  }

  // Clear specific cache pattern
  clearCachePattern(pattern: string): void {
    this.clearCache(pattern);
  }

  // Health check
  async healthCheck(): Promise<{ status: string; timestamp: number }> {
    try {
      await this.get<any>('/health', { cache: false });
      return {
        status: 'healthy',
        timestamp: Date.now()
      };
    } catch (error) {
      return {
        status: 'unhealthy',
        timestamp: Date.now()
      };
    }
  }

  // Check if a candidate profile has already been unlocked (pay once per profile)
  async checkProfileAlreadyUnlocked(featureKey: string, targetUserId: string): Promise<{ is_unlocked: boolean }> {
    const normalizedKey = featureKey === 'candidate_profile_view' ? 'PROFILE_DETAIL_VIEW' : featureKey;
    return this.get<{ status: boolean; data: { is_unlocked: boolean; already_unlocked?: boolean } }>(
      `/credits/feature/${normalizedKey}/check-unlocked?target_user_id=${targetUserId}`,
      { cache: false },
    ).then(res => ({
      is_unlocked: res.data?.is_unlocked ?? res.data?.already_unlocked ?? false,
    }));
  }

  async unlockCandidateContact(candidateId: number | string, type?: 'email' | 'phone' | 'all'): Promise<{
    status: boolean;
    data: {
      candidate_id: number;
      email: string | null;
      phone_number: string | null;
      has_email?: boolean;
      has_phone?: boolean;
      contact_unlocked: boolean;
      email_unlocked?: boolean;
      phone_unlocked?: boolean;
      feature_key?: string;
    };
    message: string;
  }> {
    const unlock_contact = type === 'email' ? 'unlock_email' : type === 'phone' ? 'unlock_phone' : 'unlock_contact';
    return this.post('/users/unlock-contact', { candidate_id: candidateId, type: type || 'all', unlock_contact });
  }

  // Deduct credits and record usage/transaction when consuming a feature
  async consumeFeature(
    featureKey: string,
    targetUserId?: string | number,
    additionalData: Record<string, any> = {}
  ): Promise<ApiResponse<any>> {
    const normalizedKey = featureKey === 'candidate_profile_view' ? 'candidate_profile_view' : featureKey;
    const body: Record<string, any> = { ...additionalData };
    if (targetUserId !== undefined && targetUserId !== null) {
      body.target_user_id = targetUserId;
      body.targetUserId = targetUserId;
      body.candidate_id = targetUserId;
    }
    return this.post<ApiResponse<any>>(`/credits/feature/${normalizedKey}/use`, body);
  }

  // Retry with exponential backoff
  async retryRequest<T>(
    requestFn: () => Promise<T>,
    config: RetryConfig = { retries: 3, retryDelay: 1000 }
  ): Promise<T> {
    const { retries, retryDelay, retryCondition } = config;

    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        return await requestFn();
      } catch (error: any) {
        const shouldRetry = retryCondition ? retryCondition(error) :
          (error.response?.status >= 500 || !error.response);

        if (attempt === retries || !shouldRetry) {
          throw error;
        }

        // Exponential backoff
        const delay = retryDelay * Math.pow(2, attempt);
        await this.delay(delay);
      }
    }

    throw new Error('Max retries exceeded');
  }

  // Batch requests with concurrency control
  async batchRequests<T>(
    requests: (() => Promise<T>)[],
    concurrency: number = 5
  ): Promise<T[]> {
    const results: T[] = [];

    for (let i = 0; i < requests.length; i += concurrency) {
      const batch = requests.slice(i, i + concurrency);
      const batchResults = await Promise.allSettled(batch.map(req => req()));

      batchResults.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          results[i + index] = result.value;
        } else {
          console.error(`Batch request ${i + index} failed:`, result.reason);
          throw result.reason;
        }
      });
    }

    return results;
  }

  // Upload with progress tracking
  async uploadWithProgress<T>(
    url: string,
    file: File,
    onProgress?: (progress: number) => void,
    onCancel?: () => void
  ): Promise<T> {
    const formData = new FormData();
    formData.append('file', file);

    const cancelToken = axios.CancelToken.source();
    const requestId = this.generateRequestId();
    this.cancelTokens.set(requestId, cancelToken);

    try {
      const response = await this.api.request<T>({
        method: 'POST',
        url,
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        cancelToken: cancelToken.token,
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(progress);
          }
        },
      });

      return response.data;
    } catch (error) {
      if (axios.isCancel(error)) {
        onCancel?.();
      }
      throw error;
    } finally {
      this.cancelTokens.delete(requestId);
    }
  }

  // Download with progress tracking
  async downloadWithProgress(
    url: string,
    onProgress?: (progress: number) => void
  ): Promise<Blob> {
    const cancelToken = axios.CancelToken.source();
    const requestId = this.generateRequestId();
    this.cancelTokens.set(requestId, cancelToken);

    try {
      const response = await this.api.request({
        method: 'GET',
        url,
        responseType: 'blob',
        cancelToken: cancelToken.token,
        onDownloadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(progress);
          }
        },
      });

      return response.data;
    } finally {
      this.cancelTokens.delete(requestId);
    }
  }

  // Upload image to backend (which handles S3 upload)
  async uploadImage(
    file: File,
    folder: string = 'profile',
    onProgress?: (progress: number) => void,
    companyInfo?: { companyId?: number; companyName?: string; userId?: number | string }
  ): Promise<{ url: string }> {
    const s3Folder = getCompanyS3Folder(folder, companyInfo?.userId || companyInfo?.companyId);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', s3Folder);

    // Add company information if provided
    if (companyInfo?.companyId) {
      formData.append('companyId', companyInfo.companyId.toString());
    }
    if (companyInfo?.companyName) {
      formData.append('companyName', companyInfo.companyName);
    }
    if (companyInfo?.userId) {
      formData.append('userId', companyInfo.userId.toString());
    }

    const cancelToken = axios.CancelToken.source();
    const requestId = this.generateRequestId();
    this.cancelTokens.set(requestId, cancelToken);

    try {
      const response = await this.api.post<{ status: boolean; data: { url: string }; message: string }>(
        '/upload/image',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          cancelToken: cancelToken.token,
          onUploadProgress: (progressEvent) => {
            if (onProgress && progressEvent.total) {
              const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              onProgress(progress);
            }
          },
        }
      );

      if (response.data.status && response.data.data?.url) {
        return { url: response.data.data.url };
      } else {
        throw new Error(response.data.message || 'Upload failed');
      }
    } finally {
      this.cancelTokens.delete(requestId);
    }
  }

  // Upload video to backend (which handles S3 upload)
  async uploadVideo(
    file: File,
    folder: string = 'pitch-video',
    maxSizeMB: number = 5,
    onProgress?: (progress: number) => void,
    companyInfo?: { companyId?: number; companyName?: string; userId?: number | string }
  ): Promise<{ url: string }> {
    const s3Folder = getCompanyS3Folder(folder, companyInfo?.userId || companyInfo?.companyId);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', s3Folder);
    formData.append('maxSizeMB', maxSizeMB.toString());

    // Add company information if provided
    if (companyInfo?.companyId) {
      formData.append('companyId', companyInfo.companyId.toString());
    }
    if (companyInfo?.companyName) {
      formData.append('companyName', companyInfo.companyName);
    }
    if (companyInfo?.userId) {
      formData.append('userId', companyInfo.userId.toString());
    }

    const cancelToken = axios.CancelToken.source();
    const requestId = this.generateRequestId();
    this.cancelTokens.set(requestId, cancelToken);

    try {


      const response = await this.api.post<{ status: boolean; data: { url: string }; message: string }>(
        '/upload/video',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          cancelToken: cancelToken.token,
          timeout: 300000, // 5 minutes timeout for large videos
          onUploadProgress: (progressEvent) => {
            if (onProgress && progressEvent.total) {
              const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              onProgress(progress);

            }
          },
        }
      );

      if (response.data.status && response.data.data?.url) {

        return { url: response.data.data.url };
      } else {
        throw new Error(response.data.message || 'Upload failed');
      }
    } catch (error: any) {
      console.error('[API Service] Video upload error:', error);
      throw error;
    } finally {
      this.cancelTokens.delete(requestId);
    }
  }


}

export const apiService = new ApiService();
